import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useCurrentUser } from '../hooks/useUserQueries';
import { useCalendarEvents, useMarkAvailability, useRemoveAvailability } from '../hooks/useCalendar';
import { useJob } from '../hooks/useJobs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import Header from '../components/layout/Header';
import PendingVerification from '../components/common/PendingVerification';
import PostJobModal from '../components/calendar/PostJobModal';
import JobDetailsModal from '../components/calendar/JobDetailsModal';
import { getEvents, addEvent, deleteEvent, saveEvents, clearAllEvents } from '../utils/calendarStorage';
import './Dashboard.css';

const Dashboard = () => {
  const { user: authUser } = useAuth();
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const calendarRef = useRef(null);
  
  // Use currentUser from API if available, otherwise fall back to authUser
  const user = currentUser || authUser;
  const userRole = user?.role_name;
  const userId = user?.id || 1; // Fallback to 1 for mock data
  
  // Check if user is verified by admin (default to false if user data is not available)
  const isVerifiedByAdmin = user?.is_verified_by_admin ?? false;
  
  // State
  const [events, setEvents] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isPostJobModalOpen, setIsPostJobModalOpen] = useState(false);
  const [isJobDetailsModalOpen, setIsJobDetailsModalOpen] = useState(false);
  const [eventsUpdateTrigger, setEventsUpdateTrigger] = useState(0);
  const [selectedJobDate, setSelectedJobDate] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState(null); // For list view
  const [modalView, setModalView] = useState('detail'); // 'detail' or 'list'

  // Fetch calendar events from service
  const { data: calendarEvents, refetch: refetchCalendarEvents } = useCalendarEvents(
    userId,
    userRole,
    { enabled: isVerifiedByAdmin }
  );

  // Migrate legacy global calendar events (if any) to per-user storage then remove legacy key
  useEffect(() => {
    if (!userId) return;
    try {
      const legacyKey = 'locumloop_calendar_events';
      const legacyJson = localStorage.getItem(legacyKey);
      if (legacyJson) {
        const legacyEvents = JSON.parse(legacyJson) || [];
        const existing = getEvents(userId);
        if (!existing || existing.length === 0) {
          saveEvents(legacyEvents, userId);
        }
        localStorage.removeItem(legacyKey);
      }
    } catch (err) {
      console.error('Error migrating legacy calendar events:', err);
    }
  }, [userId]);

  const markAvailabilityMutation = useMarkAvailability();
  const removeAvailabilityMutation = useRemoveAvailability();

  // Normalize date string to YYYY-MM-DD format
  const normalizeDate = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Intercept "more" link clicks and prevent FullCalendar popover creation
  useEffect(() => {
    const interceptMoreLinkClick = (e) => {
      const target = e.target;
      // Check if this is a "more" link
      if (target && (target.classList.contains('fc-more-link') || target.closest('.fc-more-link'))) {
        const moreLink = target.classList.contains('fc-more-link') ? target : target.closest('.fc-more-link');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Get the date from the link's parent day element
        const dayEl = moreLink.closest('.fc-daygrid-day');
        if (dayEl) {
          const dateStr = dayEl.getAttribute('data-date');
          if (dateStr) {
            const normalizedClickedDate = normalizeDate(dateStr);
            
            // Get all events on this date (excluding availability)
            const eventsOnDate = events.filter(evt => {
              const eventStart = evt.start ? normalizeDate(evt.start.split('T')[0]) : '';
              return eventStart === normalizedClickedDate && 
                     evt.extendedProps?.type !== 'availability' && 
                     evt.title !== 'Available';
            });
            
            if (eventsOnDate.length > 0) {
              setSelectedDateEvents(eventsOnDate.map(evt => ({
                id: evt.id,
                title: evt.title,
                start: evt.start,
                extendedProps: evt.extendedProps || {}
              })));
              setModalView('list');
              setSelectedEvent(null);
              setSelectedJob(null);
              setIsJobDetailsModalOpen(true);
            }
          }
        }
        return false;
      }
    };

    // Add event listener to document to catch all "more" link clicks before FullCalendar processes them
    document.addEventListener('click', interceptMoreLinkClick, true); // Use capture phase
    
    return () => {
      document.removeEventListener('click', interceptMoreLinkClick, true);
    };
  }, [events]);

  // Merge calendar events from service with local availability
  useEffect(() => {
    // Get local availability events (only user-selected ones)
    const savedEvents = getEvents(userId);
    const localAvailabilityEvents = savedEvents.filter(e => e.title === 'Available');
    
    // Filter out availability events from service (they're just mock data)
    // Only keep non-availability events from service
    const serviceEvents = (calendarEvents || []).filter(
      e => {
        const eventType = e.extendedProps?.type || e.type;
        return eventType !== 'availability';
      }
    );
    
    
    // Ensure service events are properly formatted for FullCalendar
    const formattedServiceEvents = serviceEvents.map(e => {
      const eventType = e.extendedProps?.type || e.type;
      return {
        id: e.id,
        title: e.title,
        start: normalizeDate(e.start),
        allDay: e.allDay !== false,
        backgroundColor: e.backgroundColor,
        borderColor: e.borderColor || e.backgroundColor,
        textColor: '#ffffff',
        display: 'block',
        extendedProps: {
          type: eventType,
          match_id: e.extendedProps?.match_id || e.match_id,
          job_id: e.extendedProps?.job_id || e.job_id,
          ...(e.extendedProps || {})
        }
      };
    });
    
    // Merge service events with local availability
    const mergedEvents = [
      ...formattedServiceEvents,
      ...localAvailabilityEvents.map(e => ({
        ...e,
        id: e.id || `avail-${Date.now()}-${Math.random()}`,
        title: 'Available',
        start: normalizeDate(e.start ? e.start.split('T')[0] : e.start),
        allDay: true,
        backgroundColor: '#20b2aa',
        borderColor: '#008b8b',
        textColor: '#ffffff',
        display: 'block',
        classNames: ['availability-event'],
        extendedProps: {
          ...e.extendedProps,
          type: 'availability',
          isAvailability: true
        }
      }))
    ];

    setEvents(mergedEvents);
    
    // Only count availability dates from local storage (user-selected ones)
    const availabilityDates = localAvailabilityEvents
      .map(e => normalizeDate(e.start ? e.start.split('T')[0] : e.start))
      .filter(date => date); // Remove any empty dates
    setAvailableDates([...new Set(availabilityDates)]);
  }, [calendarEvents, eventsUpdateTrigger, userId]);

  // Check if a date is in the future
  const isFutureDate = (dateStr) => {
    const clickedDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    clickedDate.setHours(0, 0, 0, 0);
    return clickedDate > today;
  };

  // Handle date click
  const handleDateClick = async (arg) => {
    arg.jsEvent?.preventDefault();
    
    const clickedDate = arg.dateStr;
    const normalizedClickedDate = normalizeDate(clickedDate);
    
    // Check if there are multiple events on this date (excluding availability)
    const eventsOnDate = events.filter(e => {
      const eventStart = e.start ? normalizeDate(e.start.split('T')[0]) : '';
      return eventStart === normalizedClickedDate && 
             e.extendedProps?.type !== 'availability' && 
             e.title !== 'Available';
    });
    
    // If there are multiple events, show list view
    if (eventsOnDate.length > 1) {
      setSelectedDateEvents(eventsOnDate.map(e => ({
        id: e.id,
        title: e.title,
        start: e.start,
        extendedProps: e.extendedProps || {}
      })));
      setModalView('list');
      setSelectedEvent(null);
      setSelectedJob(null);
      setIsJobDetailsModalOpen(true);
      return;
    }
    
    // If there's exactly one non-availability event, show its details
    if (eventsOnDate.length === 1) {
      const singleEvent = eventsOnDate[0];
      setSelectedEvent({
        id: singleEvent.id,
        title: singleEvent.title,
        start: singleEvent.start,
        extendedProps: singleEvent.extendedProps || {}
      });
      
      const eventType = singleEvent.extendedProps?.type;
      if (eventType === 'posted_job' || eventType === 'accepted_job' || eventType === 'potential_match') {
        const jobId = singleEvent.extendedProps?.job_id;
        const jobFromEvent = singleEvent.extendedProps?.job;
        if (jobFromEvent) {
          setSelectedJob(jobFromEvent);
        } else if (jobId) {
          setSelectedJob({ id: jobId });
        }
      }
      
      setModalView('detail');
      setSelectedDateEvents(null);
      setIsJobDetailsModalOpen(true);
      return;
    }
    
    // For employees: mark availability (only when no non-availability events exist)
    if (userRole === 'employee') {
      if (!isFutureDate(normalizedClickedDate)) {
        return;
      }
      
      const existingAvailability = events.find(e => {
        const eventStart = e.start ? normalizeDate(e.start.split('T')[0]) : '';
        return eventStart === normalizedClickedDate && 
               (e.extendedProps?.type === 'availability' || e.title === 'Available');
      });
      
      if (existingAvailability) {
        // Remove availability from local storage
        deleteEvent(existingAvailability.id, userId);
        
        // Trigger events update to re-merge from local storage
        setEventsUpdateTrigger(prev => prev + 1);
        
        // Also remove from service
        try {
          await removeAvailabilityMutation.mutateAsync({
            employeeId: userId,
            date: normalizedClickedDate
          });
          // Refetch calendar events to ensure sync
          refetchCalendarEvents();
        } catch (error) {
          console.error('Error removing availability:', error);
        }
      } else {
        // Add availability
        const newEvent = {
          id: `avail-${Date.now()}`,
          title: 'Available',
          start: normalizedClickedDate,
          allDay: true,
          backgroundColor: '#20b2aa',
          borderColor: '#008b8b',
          textColor: '#ffffff',
          display: 'block',
          classNames: ['availability-event'],
          extendedProps: {
            type: 'availability',
            isAvailability: true
          }
        };
        
        // Add availability to local storage
        addEvent(newEvent, userId);
        
        // Trigger events update to re-merge from local storage
        setEventsUpdateTrigger(prev => prev + 1);
        
        // Also add to service
        try {
          await markAvailabilityMutation.mutateAsync({
            employeeId: userId,
            date: normalizedClickedDate
          });
          // Refetch calendar events to ensure sync
          refetchCalendarEvents();
        } catch (error) {
          console.error('Error marking availability:', error);
        }
      }
    } 
    // For employers: open post job modal
    else if (userRole === 'employer') {
      if (!isFutureDate(normalizedClickedDate)) {
        return;
      }
      // Pre-fill the clicked date in the modal
      setSelectedJobDate(normalizedClickedDate);
      setIsPostJobModalOpen(true);
    }
  };

  // Handle event click
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const eventType = event.extendedProps?.type;
    
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      extendedProps: event.extendedProps || {}
    });

    // If it's a job-related event, use the job data from event if available
    if (eventType === 'posted_job' || eventType === 'accepted_job' || eventType === 'potential_match') {
      const jobId = event.extendedProps?.job_id;
      // Use job data from event if available, otherwise just pass the ID
      const jobFromEvent = event.extendedProps?.job;
      if (jobFromEvent) {
        setSelectedJob(jobFromEvent);
      } else if (jobId) {
        // Job details will be fetched in JobDetailsModal
        setSelectedJob({ id: jobId });
      }
    }

    setModalView('detail');
    setSelectedDateEvents(null);
    setIsJobDetailsModalOpen(true);
  };


  // Handle post job success
  const handlePostJobSuccess = () => {
    refetchCalendarEvents();
  };

  // Handle job details modal close
  const handleJobDetailsClose = () => {
    setIsJobDetailsModalOpen(false);
    setSelectedEvent(null);
    setSelectedJob(null);
    setSelectedDateEvents(null);
    setModalView('detail');
    refetchCalendarEvents();
  };

  // Handle event selection from list view
  const handleEventSelectFromList = (event) => {
    setSelectedEvent(event);
    setModalView('detail');
    
    // If it's a job-related event, use the job data from event if available
    const eventType = event.extendedProps?.type;
    if (eventType === 'posted_job' || eventType === 'accepted_job' || eventType === 'potential_match') {
      const jobId = event.extendedProps?.job_id;
      const jobFromEvent = event.extendedProps?.job;
      if (jobFromEvent) {
        setSelectedJob(jobFromEvent);
      } else if (jobId) {
        setSelectedJob({ id: jobId });
      }
    }
  };

  if (isLoadingUser) {
    return (
      <div className="dashboard">
        <Header />
        <div className="dashboard__content">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Show pending verification page if user is not verified by admin
  if (!isVerifiedByAdmin) {
    return <PendingVerification />;
  }

  const getInfoText = () => {
    if (userRole === 'employee') {
      return 'Click on dates to mark them as available. Click on events to view details.';
    } else if (userRole === 'employer') {
      return 'Click on dates to post a job. Click on events to view job details.';
    }
    return '';
  };

  // Calculate statistics for the side panel
  const calculateStats = () => {
    const stats = {
      totalEvents: events.filter(e => e.extendedProps?.type !== 'availability' && e.title !== 'Available').length,
      postedJobs: events.filter(e => e.extendedProps?.type === 'posted_job').length,
      potentialMatches: events.filter(e => e.extendedProps?.type === 'potential_match').length,
      acceptedJobs: events.filter(e => e.extendedProps?.type === 'accepted_job').length,
      completedJobs: events.filter(e => e.extendedProps?.type === 'completed_job').length,
      availableDates: availableDates.length,
    };

    return stats;
  };

  const stats = calculateStats();

  return (
    <div className="dashboard">
      <Header />
      
      <div className="dashboard__content">
        <div className="dashboard__layout">
          <div className="dashboard__calendar-section">

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: ''
            }}
            events={events}
            editable={false}
            selectable={false}
            unselectAuto={true}
            dayMaxEvents={1}
            weekends={true}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            selectMirror={false}
            selectOverlap={false}
            eventOverlap={false}
            moreLinkClick={(arg) => {
              // Prevent FullCalendar from creating popover - we handle it via DOM events
              if (arg.jsEvent) {
                arg.jsEvent.preventDefault();
                arg.jsEvent.stopPropagation();
              }
              return false;
            }}
            height="auto"
            locale="en-GB"
            firstDay={1}
            eventDisplay="block"
            dayCellDidMount={(arg) => {
              const dateStr = arg.dateStr;
              if (!dateStr) return;
              
              const normalizedDateStr = normalizeDate(dateStr);
              const isAvailable = availableDates.some(date => {
                const normalizedDate = normalizeDate(date);
                return normalizedDate === normalizedDateStr;
              });
              
              const isPastOrToday = !isFutureDate(normalizedDateStr);
              
              if (isAvailable) {
                arg.el.classList.add('available-date');
              }
              if (isPastOrToday && !isAvailable) {
                arg.el.classList.add('past-date');
              }
            }}
          />
          </div>

          {/* Stats Side Panel */}
          <div className="dashboard__stats-panel">
            <div className="dashboard__stats-content">
              {/* Quick Actions - Only for employers */}
              {userRole === 'employer' && (
                <div className="dashboard__quick-actions">
                  <h4 className="dashboard__quick-actions-title">Quick Actions</h4>
                  <button
                    className="dashboard__quick-action-button"
                    onClick={() => {
                      const today = new Date();
                      const todayStr = today.toISOString().split('T')[0];
                      setSelectedJobDate(todayStr);
                      setIsPostJobModalOpen(true);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Post New Job
                  </button>
                  <div className="dashboard__quick-info">
                    <p className="dashboard__quick-info-text">{getInfoText()}</p>
                  </div>
                </div>
              )}
              
              {/* Info text for employees (without quick actions section) */}
              {userRole === 'employee' && (
                <div className="dashboard__quick-info" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <p className="dashboard__quick-info-text">{getInfoText()}</p>
                </div>
              )}

              {/* Overview Section - Second for context */}
              <div className="dashboard__overview-section">
                <h3 className="dashboard__overview-title">Overview</h3>
                {/* Quick Stats Cards */}
              <div className="dashboard__stats-grid">
                {userRole === 'employer' ? (
                  <div className="dashboard__stat-card">
                    <div className="dashboard__stat-icon" style={{ backgroundColor: '#fee2e2' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="12" y1="18" x2="12" y2="12"></line>
                        <line x1="9" y1="15" x2="15" y2="15"></line>
                      </svg>
                    </div>
                    <div className="dashboard__stat-info">
                      <div className="dashboard__stat-value">{stats.postedJobs}</div>
                      <div className="dashboard__stat-label">Posted Jobs</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="dashboard__stat-card">
                      <div className="dashboard__stat-icon" style={{ backgroundColor: '#e0f2fe' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </div>
                      <div className="dashboard__stat-info">
                        <div className="dashboard__stat-value">{stats.totalEvents}</div>
                        <div className="dashboard__stat-label">Total Events</div>
                      </div>
                    </div>
                    <div className="dashboard__stat-card">
                      <div className="dashboard__stat-icon" style={{ backgroundColor: '#d1fae5' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                      <div className="dashboard__stat-info">
                        <div className="dashboard__stat-value">{stats.availableDates}</div>
                        <div className="dashboard__stat-label">Available Dates</div>
                      </div>
                    </div>
                  </>
                )}

                <div className="dashboard__stat-card">
                  <div className="dashboard__stat-icon" style={{ backgroundColor: '#fef3c7' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                  <div className="dashboard__stat-info">
                    <div className="dashboard__stat-value">{stats.potentialMatches}</div>
                    <div className="dashboard__stat-label">Potential Matches</div>
                  </div>
                </div>

                <div className="dashboard__stat-card">
                  <div className="dashboard__stat-icon" style={{ backgroundColor: '#dbeafe' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                  <div className="dashboard__stat-info">
                    <div className="dashboard__stat-value">{stats.acceptedJobs}</div>
                    <div className="dashboard__stat-label">Accepted</div>
                  </div>
                </div>

                <div className="dashboard__stat-card">
                  <div className="dashboard__stat-icon" style={{ backgroundColor: '#e9d5ff' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div className="dashboard__stat-info">
                    <div className="dashboard__stat-value">{stats.completedJobs}</div>
                    <div className="dashboard__stat-label">Completed</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend - Last as reference material */}
            <div className="dashboard__legend-section">
                <h4 className="dashboard__legend-title">Event Status</h4>
                <div className="dashboard__legend">
                  {userRole === 'employee' && (
                    <>
                      <div className="dashboard__legend-item">
                        <span className="dashboard__legend-color" style={{ backgroundColor: '#20b2aa' }}></span>
                        <span>Available</span>
                      </div>
                      <div className="dashboard__legend-item">
                        <span className="dashboard__legend-color" style={{ backgroundColor: '#ffa500' }}></span>
                        <span>Potential Match</span>
                      </div>
                      <div className="dashboard__legend-item">
                        <span className="dashboard__legend-color" style={{ backgroundColor: '#4169e1' }}></span>
                        <span>Accepted Job</span>
                      </div>
                      <div className="dashboard__legend-item">
                        <span className="dashboard__legend-color" style={{ backgroundColor: '#9370db' }}></span>
                        <span>Completed Job</span>
                      </div>
                    </>
                  )}
                  {userRole === 'employer' && (
                    <>
                      <div className="dashboard__legend-item">
                        <span className="dashboard__legend-color" style={{ backgroundColor: '#ff6347' }}></span>
                        <span>Posted Job</span>
                      </div>
                      <div className="dashboard__legend-item">
                        <span className="dashboard__legend-color" style={{ backgroundColor: '#ffa500' }}></span>
                        <span>Potential Match</span>
                      </div>
                      <div className="dashboard__legend-item">
                        <span className="dashboard__legend-color" style={{ backgroundColor: '#4169e1' }}></span>
                        <span>Accepted Job</span>
                      </div>
                      <div className="dashboard__legend-item">
                        <span className="dashboard__legend-color" style={{ backgroundColor: '#9370db' }}></span>
                        <span>Completed Job</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <PostJobModal
        isOpen={isPostJobModalOpen}
        onClose={() => {
          setIsPostJobModalOpen(false);
          setSelectedJobDate(null); // Reset selected date when closing
        }}
        onSuccess={handlePostJobSuccess}
        initialDate={selectedJobDate}
      />

      <JobDetailsModal
        isOpen={isJobDetailsModalOpen}
        onClose={handleJobDetailsClose}
        event={selectedEvent}
        job={selectedJob}
        events={selectedDateEvents}
        view={modalView}
        onEventSelect={handleEventSelectFromList}
        onBackToList={() => {
          setModalView('list');
          setSelectedEvent(null);
          setSelectedJob(null);
        }}
        onRemoveAvailability={async (dateStr) => {
          if (!dateStr) return;
          const normalizedDate = normalizeDate(dateStr);
          
          // Find the availability event
          const availabilityEvent = events.find(e => {
            const eventStart = e.start ? normalizeDate(e.start.split('T')[0]) : '';
            return eventStart === normalizedDate && 
                   (e.extendedProps?.type === 'availability' || e.title === 'Available');
          });
          
          if (availabilityEvent) {
            // Remove from local storage
            deleteEvent(availabilityEvent.id, userId);
            
            // Trigger events update
            setEventsUpdateTrigger(prev => prev + 1);
            
            // Remove from backend
            try {
              await removeAvailabilityMutation.mutateAsync({
                employeeId: userId,
                date: normalizedDate
              });
              // Refetch calendar events to ensure sync
              refetchCalendarEvents();
            } catch (error) {
              console.error('Error removing availability:', error);
              throw error;
            }
          }
        }}
      />
    </div>
  );
};

export default Dashboard;
